import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, FormHelperText, Link as MuiLink, OutlinedInput, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import UserAuthService from '../../../shared/services/user-auth/user-auth.service';
import { IMfaMethod, IMfaMethods } from '../../../shared/services/user-auth/user-auth.model';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { AuthLayout } from './auth/auth-layout';
import { useAuthBranding } from './auth/use-auth-branding';
import { AUTH_TOKENS, BRAND_DISPLAY_FONT } from './auth/auth-branding';

const RESEND_COOLDOWN_SECONDS = 30;
const CODE_LENGTH = 6;

/**
 * Second-factor challenge shown after a password login when the account has MFA
 * enabled. Mounted at /login/verify (a public route): the session is held in the
 * "MFA pending" state until a code verifies, so the guard treats the user as not
 * yet signed in. Mirrors email-verification-page for the code-entry UX.
 */
export const MfaChallengePage = () => {
  const userAuthService = new UserAuthService();
  const userDetailsContext = useContext(UserDetailsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useAuthBranding();

  const [methods, setMethods] = useState<IMfaMethods | null>((location.state as any)?.methods ?? null);
  // The primary factor the 6-digit boxes submit as. Backup codes are a separate
  // entry mode below.
  const [activeMethod, setActiveMethod] = useState<'totp' | 'email'>('totp');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [backupCode, setBackupCode] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Resolve the available methods. Prefer the ones handed over by the login step;
  // on a hard reload of this route, re-fetch them (GET /user is reachable while
  // pending and reports mfaPending + mfaMethods). If the session is no longer
  // pending, the second factor is already done, so continue into the app.
  useEffect(() => {
    if (methods) {
      setActiveMethod(methods.totp ? 'totp' : 'email');
      return;
    }
    userDetailsContext
      .forceUpdateUserDetails()
      .then((details) => {
        if (details?.mfaPending && details?.mfaMethods) {
          setMethods(details.mfaMethods);
          setActiveMethod(details.mfaMethods.totp ? 'totp' : 'email');
        } else if (details && !details.mfaPending) {
          navigate(details.isPartner ? '/partner-dashboard' : '/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      })
      .catch(() => navigate('/login', { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!useBackupCode) {
      inputsRef.current[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBackupCode]);

  // Resend cooldown tick.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const completeSignIn = async () => {
    const updated = await userDetailsContext.forceUpdateUserDetails();
    navigate(
      updated?.isPartner ? '/partner-dashboard' : '/dashboard',
      updated?.isPartner ? undefined : { state: { justAuthenticated: true } },
    );
  };

  const submitCode = async (code: string, method: IMfaMethod) => {
    setInProgress(true);
    setErrorMessage('');
    try {
      await userAuthService.verifyMfa({ code, method });
      await completeSignIn();
    } catch (err: any) {
      setInProgress(false);
      setDigits(Array(CODE_LENGTH).fill(''));
      if (!useBackupCode) {
        inputsRef.current[0]?.focus();
      }
      setErrorMessage(err?.response?.data?.message || 'That code is not right. Please try again.');
    }
  };

  const handleDigitChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const value = (e.target as HTMLInputElement).value.replace(/\D/g, '');
    const next = [...digits];
    next[index] = value.slice(-1);
    setDigits(next);
    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
    const joined = next.join('');
    if (joined.length === CODE_LENGTH && !next.includes('') && !inProgress) {
      submitCode(joined, activeMethod);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    if (e.key === 'Backspace' && !(e.target as HTMLInputElement).value && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const chars = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, CODE_LENGTH).split('');
    if (!chars.length) return;
    const next = Array(CODE_LENGTH).fill('').map((_, i) => chars[i] ?? '');
    setDigits(next);
    if (chars.length === CODE_LENGTH && !inProgress) {
      submitCode(next.join(''), activeMethod);
    }
  };

  const handleBackupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!backupCode.trim() || inProgress) return;
    submitCode(backupCode.trim(), 'backup');
  };

  const resendEmailCode = async () => {
    if (cooldown > 0) return;
    setErrorMessage('');
    try {
      const response = await userAuthService.resendMfaLoginCode();
      setSuccessMessage(response?.message || 'A new code has been sent to your email.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setErrorMessage(err?.response?.data?.message || 'We could not send a new code. Please try again.');
    }
  };

  const bothMethods = !!methods?.totp && !!methods?.email;
  const title = useBackupCode
    ? 'Enter a backup code'
    : activeMethod === 'email'
      ? 'Check your email'
      : 'Two-step verification';
  const subtitle = useBackupCode
    ? 'Enter one of the single-use backup codes you saved when you set up the authenticator app.'
    : activeMethod === 'email'
      ? 'Enter the 6-digit code we just sent to your email address.'
      : 'Enter the 6-digit code from your authenticator app.';

  const codeInputSx = {
    width: { xs: 44, sm: 52 },
    '& .MuiOutlinedInput-root': {
      height: 56,
      borderRadius: '11px',
      backgroundColor: 'common.white',
      '& input': { textAlign: 'center', fontSize: '1.35rem', p: 0 },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: AUTH_TOKENS.border },
    '& .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: `0 0 0 3px ${branding.focusRing}`,
      '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1px', borderColor: 'primary.main' },
    },
  } as const;

  return (
    <AuthLayout branding={branding}>
      <Typography
        component="h1"
        sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
      >
        {title}
      </Typography>
      <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
        {subtitle}
      </Typography>

      {!useBackupCode ? (
        <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: '16px' }} onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <OutlinedInput
              key={index}
              inputRef={(el) => { inputsRef.current[index] = el as HTMLInputElement | null; }}
              value={digit}
              onChange={(e) => handleDigitChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              placeholder="·"
              inputProps={{ maxLength: 1, inputMode: 'numeric', 'aria-label': `Digit ${index + 1}` }}
              sx={codeInputSx}
            />
          ))}
        </Stack>
      ) : (
        <Box component="form" onSubmit={handleBackupSubmit} sx={{ mb: '16px' }}>
          <OutlinedInput
            autoFocus
            fullWidth
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value)}
            placeholder="xxxxx-xxxxx"
            inputProps={{ 'aria-label': 'Backup code', autoCapitalize: 'none', autoCorrect: 'off' }}
            sx={{ borderRadius: '11px', backgroundColor: 'common.white', '& input': { fontSize: '1.05rem', letterSpacing: '0.06em' } }}
          />
          <Button type="submit" fullWidth size="large" disabled={inProgress || !backupCode.trim()} sx={{ mt: 2, height: 50, fontSize: '0.9375rem' }}>
            {inProgress ? <CircularProgress size={20} color="inherit" /> : 'Verify'}
          </Button>
        </Box>
      )}

      {errorMessage && (
        <FormHelperText error role="alert" sx={{ ml: '2px', mb: 1, fontSize: '0.8125rem' }}>
          {errorMessage}
        </FormHelperText>
      )}

      {inProgress && !useBackupCode && !errorMessage && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, color: AUTH_TOKENS.inkSecondary }}>
          <CircularProgress size={16} color="inherit" />
          <Typography sx={{ fontSize: '0.8125rem' }}>Verifying…</Typography>
        </Stack>
      )}

      <Stack spacing={0.5} sx={{ mt: '8px' }}>
        {!useBackupCode && activeMethod === 'email' && (
          <Typography sx={{ fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary }}>
            Didn&apos;t get it?{' '}
            <MuiLink
              component="button"
              type="button"
              onClick={resendEmailCode}
              disabled={cooldown > 0}
              underline="none"
              sx={{ fontWeight: 500, color: cooldown > 0 ? AUTH_TOKENS.inkTertiary : 'primary.main', cursor: cooldown > 0 ? 'default' : 'pointer', verticalAlign: 'baseline' }}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </MuiLink>
          </Typography>
        )}

        {!useBackupCode && bothMethods && (
          <MuiLink
            component="button"
            type="button"
            onClick={() => { setErrorMessage(''); setDigits(Array(CODE_LENGTH).fill('')); setActiveMethod(activeMethod === 'totp' ? 'email' : 'totp'); }}
            underline="none"
            sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'primary.main', textAlign: 'left' }}
          >
            {activeMethod === 'totp' ? 'Use an emailed code instead' : 'Use your authenticator app instead'}
          </MuiLink>
        )}

        {methods?.totp && (
          <MuiLink
            component="button"
            type="button"
            onClick={() => { setErrorMessage(''); setUseBackupCode(!useBackupCode); }}
            underline="none"
            sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'primary.main', textAlign: 'left' }}
          >
            {useBackupCode ? 'Back to entering a code' : 'Use a backup code instead'}
          </MuiLink>
        )}
      </Stack>

      {successMessage && (
        <FormHelperText role="status" sx={{ mt: 1, ml: '2px', color: 'primary.main', fontSize: '0.8125rem' }}>
          {successMessage}
        </FormHelperText>
      )}
    </AuthLayout>
  );
};
